import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import './PrivacyPolicy.css';
import logoWhite from '../../images/logo_white.png';

const PrivacyPolicy = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  const sections = [
    { id: 'introduction', label: 'Introduction' },
    { id: 'information-we-collect', label: 'Information We Collect' },
    { id: 'how-we-use', label: 'How We Use Your Information' },
    { id: 'data-storage', label: 'Data Storage and Security' },
    { id: 'data-sharing', label: 'Data Sharing and Disclosure' },
    { id: 'cookies', label: 'Cookies and Tracking' },
    { id: 'retention', label: 'Data Retention' },
    { id: 'your-rights', label: 'Your Rights' },
    { id: 'international', label: 'International Transfers' },
    { id: 'children', label: "Children's Privacy" },
    { id: 'third-party', label: 'Third-Party Links' },
    { id: 'changes', label: 'Changes to This Policy' },
    { id: 'contact', label: 'Contact Us' },
  ];

  return (
    <div className="privacy-container">

      {/* Navbar */}
      <nav className="privacy-navbar">
        <div className="privacy-nav-content">
          <div className="privacy-logo" onClick={() => window.location.href = '/'}>
            <img src={logoWhite} alt="TalentScope360 Logo" />
          </div>

          <button
            className="privacy-mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="privacy-nav-links privacy-nav-desktop">
            <a href="/#features" className="privacy-nav-link">Features</a>
            <a href="/pricing" className="privacy-nav-link">Pricing</a>
            <a href="/documentation" className="privacy-nav-link">Documentation</a>
          </div>

          <div className="privacy-nav-buttons privacy-nav-desktop">
            <a href="/login" className="privacy-btn-login">Login</a>
            <a href="/sign-up" className="privacy-btn-getstarted">Get Started</a>
          </div>
        </div>

        <div className={`privacy-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <a href="/#features" className="privacy-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>Features</a>
          <a href="/#faq" className="privacy-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
          <a href="/documentation" className="privacy-mobile-menu-link" onClick={() => setMobileMenuOpen(false)}>Documentation</a>
          <div className="privacy-mobile-menu-buttons">
            <a href="/login" className="privacy-btn-login">Login</a>
            <a href="/sign-up" className="privacy-btn-getstarted">Get Started</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="privacy-hero">
        <div className="privacy-hero-pattern" aria-hidden="true" />
        <div className="privacy-hero-content">
          <h1 className="privacy-hero-title">Privacy Policy</h1>
          <div className="privacy-hero-accent" aria-hidden="true" />
          <p className="privacy-hero-subtitle">
            Your data belongs to you. Here is exactly how we collect, use, and protect it.
          </p>
          <div className="privacy-hero-meta">
            <span className="privacy-hero-chip">
              <span className="privacy-hero-chip-dot" />
              Effective: 1st January 2024
            </span>
            <span className="privacy-hero-chip-divider" />
            <span className="privacy-hero-chip">
              <span className="privacy-hero-chip-dot privacy-hero-chip-dot--updated" />
              Last Updated: 1st January 2026
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="privacy-body">

        {/* Sidebar TOC */}
        <aside className="privacy-sidebar">
          <p className="privacy-sidebar-title">Contents</p>
          <nav className="privacy-toc">
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="privacy-toc-link">{s.label}</a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="privacy-main">

          <div className="privacy-intro-card">
            <p>
              ARTEMIS Transition Partners ("we", "us", "our") operates the TalentScope360 platform. This Privacy
              Policy explains how we collect, use, store, and protect information about you and your organization
              when you use our Service. We are committed to handling your data responsibly and transparently.
            </p>
          </div>

          {/* 1 */}
          <section id="introduction" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">01</span>
              Introduction
            </h2>
            <p>
              TalentScope360 is a competency-based talent management platform designed to help organizations
              plan, assess, and develop workforce capabilities. Given the nature of the data processed — including
              employee competency assessments and organizational structures — we take privacy seriously and have
              built our platform with data minimization, security, and user control at its core.
            </p>
            <p>
              This policy applies to all users of the TalentScope360 platform, including organization
              administrators, HR professionals, managers, and employees who are assessed through the platform.
            </p>
            <div className="privacy-highlight-box">
              <strong>Key Principle:</strong> TalentScope360 processes competency and capability data — not
              performance targets, KPIs, or payroll information. We collect only what is needed to deliver the Service.
            </div>
          </section>

          {/* 2 */}
          <section id="information-we-collect" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">02</span>
              Information We Collect
            </h2>
            <p>We collect information in the following categories:</p>

            <h3 className="privacy-subsection-heading">Account and Registration Data</h3>
            <ul className="privacy-list">
              <li>Full name and contact details (email address, phone number)</li>
              <li>Organization name, size, industry, and location</li>
              <li>Job title and role within the organization</li>
              <li>Account credentials (passwords are hashed and never stored in plain text)</li>
            </ul>

            <h3 className="privacy-subsection-heading">Organizational Data</h3>
            <ul className="privacy-list">
              <li>Organizational structure (corporate and functional hierarchies)</li>
              <li>Department and function details</li>
              <li>Role definitions and reporting lines</li>
              <li>Competency frameworks and models defined by your organization</li>
            </ul>

            <h3 className="privacy-subsection-heading">Talent and Assessment Data</h3>
            <ul className="privacy-list">
              <li>Individual competency assessment scores and ratings</li>
              <li>Evaluator responses and comments submitted during talent reviews</li>
              <li>Competency maturity levels and development gaps</li>
              <li>Succession planning and talent pipeline data</li>
            </ul>

            <h3 className="privacy-subsection-heading">Usage and Technical Data</h3>
            <ul className="privacy-list">
              <li>Log data (IP address, browser type, pages visited, timestamps)</li>
              <li>Device and operating system information</li>
              <li>Session activity and feature usage patterns</li>
              <li>Error reports and performance diagnostics</li>
            </ul>

            <h3 className="privacy-subsection-heading">Communications Data</h3>
            <ul className="privacy-list">
              <li>Messages sent via contact and demo request forms</li>
              <li>Support enquiries and correspondence</li>
              <li>Scheduled call information (name, company, date, time)</li>
            </ul>
          </section>

          {/* 3 */}
          <section id="how-we-use" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">03</span>
              How We Use Your Information
            </h2>
            <p>We use the information we collect for the following purposes:</p>

            <div className="privacy-use-grid">
              <div className="privacy-use-card">
                <h4>Service Delivery</h4>
                <p>Providing access to the TalentScope360 platform, processing assessments, generating reports, and managing your organization's talent data.</p>
              </div>
              <div className="privacy-use-card">
                <h4>Account Management</h4>
                <p>Creating and managing user accounts, authenticating users, and administering access permissions and roles.</p>
              </div>
              <div className="privacy-use-card">
                <h4>Communication</h4>
                <p>Sending service notifications, account updates, scheduled call confirmations, and responding to support enquiries.</p>
              </div>
              <div className="privacy-use-card">
                <h4>Platform Improvement</h4>
                <p>Analysing usage patterns to improve features, fix bugs, and enhance performance — using anonymized and aggregated data only.</p>
              </div>
              <div className="privacy-use-card">
                <h4>Security and Compliance</h4>
                <p>Detecting fraud, unauthorized access, and security incidents; complying with legal obligations.</p>
              </div>
              <div className="privacy-use-card">
                <h4>Billing and Payments</h4>
                <p>Processing subscription payments and managing invoicing through secure third-party payment processors.</p>
              </div>
            </div>

            <p style={{ marginTop: '1.25rem' }}>
              We do not use your competency or assessment data for purposes beyond delivering the Service to your
              organization unless required by law or with your explicit consent.
            </p>
          </section>

          {/* 4 */}
          <section id="data-storage" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">04</span>
              Data Storage and Security
            </h2>
            <p>
              TalentScope360 uses <strong>Supabase</strong> as its primary data infrastructure provider. Supabase
              provides enterprise-grade security including:
            </p>
            <ul className="privacy-list">
              <li>Data encryption at rest and in transit (TLS/SSL)</li>
              <li>Row-Level Security (RLS) ensuring each organization's data is isolated</li>
              <li>Regular automated backups</li>
              <li>SOC 2 compliant infrastructure</li>
              <li>Secure authentication with hashed credential storage</li>
            </ul>
            <p>
              We implement additional safeguards including role-based access controls, permission-gated module
              access, and audit logging of sensitive operations within the platform.
            </p>
            <div className="privacy-highlight-box">
              <strong>Note:</strong> While we apply industry-standard security measures, no system is completely
              immune to breaches. We commit to notifying affected users promptly in the event of a data incident
              involving personal information.
            </div>
          </section>

          {/* 5 */}
          <section id="data-sharing" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">05</span>
              Data Sharing and Disclosure
            </h2>
            <p>
              We do not sell, rent, or trade your personal data or your organization's talent data to third parties.
              We may share data only in the following circumstances:
            </p>
            <ul className="privacy-list">
              <li><strong>Service Providers:</strong> Trusted third-party providers (e.g., Supabase for infrastructure, EmailJS for communication) who process data on our behalf under strict data processing agreements.</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or regulatory authority.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, data may be transferred as part of that transaction. We will notify you before your data is transferred and subject to a new privacy policy.</li>
              <li><strong>With Your Consent:</strong> In any other circumstance where you have given explicit consent.</li>
            </ul>
            <p>
              Within your organization, access to talent and assessment data is governed by the permissions
              configured by your organization's administrator. Individual assessment results are only accessible
              to authorized users as defined by your organization's access model.
            </p>
          </section>

          {/* 6 */}
          <section id="cookies" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">06</span>
              Cookies and Tracking
            </h2>
            <p>
              TalentScope360 uses cookies and similar technologies to maintain your session, remember your
              preferences, and understand how the platform is used. The types of cookies we use include:
            </p>
            <ul className="privacy-list">
              <li><strong>Essential Cookies:</strong> Required for authentication, session management, and core platform functionality. These cannot be disabled.</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences such as language, sidebar state, and display settings.</li>
              <li><strong>Analytics Cookies:</strong> Collect anonymized, aggregated data about how the platform is used to help us improve the Service. No personally identifiable information is included.</li>
            </ul>
            <p>
              You can manage cookie preferences through your browser settings. Note that disabling essential
              cookies will affect your ability to use the platform.
            </p>
          </section>

          {/* 7 */}
          <section id="retention" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">07</span>
              Data Retention
            </h2>
            <p>
              We retain your data for as long as your account is active or as needed to provide the Service. Specific
              retention periods are as follows:
            </p>
            <ul className="privacy-list">
              <li><strong>Account Data:</strong> Retained for the duration of the subscription and for up to 12 months after account closure.</li>
              <li><strong>Assessment and Talent Data:</strong> Retained as configured by your organization administrator, or deleted upon account closure with a 30-day grace period for export.</li>
              <li><strong>Usage Logs:</strong> Retained for up to 12 months for security and diagnostic purposes.</li>
              <li><strong>Communication Records:</strong> Retained for up to 24 months or as required by applicable law.</li>
            </ul>
            <p>
              You may request deletion of your data at any time by contacting us. Upon verified request, we will
              delete or anonymize personal data within 30 days, except where retention is required by law.
            </p>
          </section>

          {/* 8 */}
          <section id="your-rights" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">08</span>
              Your Rights
            </h2>
            <p>
              Depending on your jurisdiction, you may have the following rights regarding your personal data:
            </p>
            <ul className="privacy-list">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten").</li>
              <li><strong>Portability:</strong> Request your data in a structured, machine-readable format.</li>
              <li><strong>Restriction:</strong> Request that we limit how we process your data in certain circumstances.</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests or for direct marketing.</li>
              <li><strong>Withdraw Consent:</strong> Where processing is based on consent, withdraw it at any time without affecting prior processing.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:support@talentscope360.com" className="privacy-link">support@talentscope360.com</a>.
              We will respond within 30 days. We may need to verify your identity before processing requests.
            </p>
            <p>
              <strong>Note for assessed employees:</strong> If your competency data was collected through your
              employer's use of TalentScope360, your primary point of contact for data rights requests is
              your organization's HR administrator.
            </p>
          </section>

          {/* 9 */}
          <section id="international" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">09</span>
              International Transfers
            </h2>
            <p>
              TalentScope360 serves organizations globally. Your data may be processed and stored in data
              centers outside your country of residence. Where data is transferred internationally, we ensure
              appropriate safeguards are in place, including standard contractual clauses or reliance on
              adequacy decisions as applicable.
            </p>
            <p>
              Organizations operating under GDPR (EU/EEA), POPIA (South Africa), CCPA (California), or other
              regional data protection regimes are responsible for ensuring their use of TalentScope360 aligns
              with their obligations under those laws. We are committed to supporting your compliance obligations.
            </p>
          </section>

          {/* 10 */}
          <section id="children" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">10</span>
              Children's Privacy
            </h2>
            <p>
              TalentScope360 is a professional enterprise platform intended exclusively for use by organizations
              and individuals aged 18 and above. We do not knowingly collect personal data from individuals
              under the age of 18.
            </p>
            <p>
              If you believe a minor has provided personal data through our platform, please contact us
              immediately at <a href="mailto:support@talentscope360.com" className="privacy-link">support@talentscope360.com</a> and
              we will take prompt steps to delete such data.
            </p>
          </section>

          {/* 11 */}
          <section id="third-party" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">11</span>
              Third-Party Links
            </h2>
            <p>
              The TalentScope360 platform and its documentation may contain links to third-party websites or
              services. We are not responsible for the privacy practices or content of those external sites.
              We encourage you to review the privacy policies of any third-party service you visit.
            </p>
          </section>

          {/* 12 */}
          <section id="changes" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">12</span>
              Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology,
              legal requirements, or other factors. When we make material changes, we will:
            </p>
            <ul className="privacy-list">
              <li>Update the "Last Updated" date at the top of this page</li>
              <li>Notify registered users by email or in-app notification</li>
              <li>Where required by law, seek renewed consent</li>
            </ul>
            <p>
              Your continued use of TalentScope360 following the posting of changes constitutes your acceptance
              of the updated Privacy Policy.
            </p>
          </section>

          {/* 13 */}
          <section id="contact" className="privacy-section">
            <h2 className="privacy-section-heading">
              <span className="privacy-section-number">13</span>
              Contact Us
            </h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or how we handle
              your data, please reach out to us:
            </p>
            <div className="privacy-contact-card">
              <p><strong>ARTEMIS Transition Partners</strong></p>
              <p>Operating: TalentScope360</p>
              <p>Email: <a href="mailto:support@talentscope360.com" className="privacy-link">support@talentscope360.com</a></p>
              <p>Website: <a href="https://www.talentscope360.com" className="privacy-link" target="_blank" rel="noreferrer">www.talentscope360.com</a></p>
            </div>
            <p style={{ marginTop: '1rem' }}>
              We are committed to resolving privacy concerns promptly. If you are not satisfied with our
              response, you may have the right to lodge a complaint with your local data protection authority.
            </p>
          </section>

          <div className="privacy-footer-note">
            <p>
              This Privacy Policy is effective as of the date stated above and governs all use of the
              TalentScope360 platform and related services.
            </p>
            <div className="privacy-legal-links">
              <a href="/terms-of-service" className="privacy-link">Terms of Service</a>
              <span className="privacy-divider">|</span>
              <a href="/" className="privacy-link">Back to Home</a>
            </div>
          </div>

        </main>
      </div>

      {/* Footer */}
      <footer className="privacy-footer-bottom">
        <p>© {currentYear} TalentScope360 | All rights reserved. | <a href="/terms-of-service" className="privacy-footer-link">Terms of Service</a> &nbsp;|&nbsp; <a href="/privacy-policy" className="privacy-footer-link">Privacy Policy</a></p>
      </footer>

    </div>
  );
};

export default PrivacyPolicy;
